/**
 * Utilitários puros de formatação, normalização de tipos e parsing
 * para a visualização e exportação XLSX do Histórico de Eventos.
 */

export const formatarDataHora = (isoString) => {
  if (!isoString) return "—";
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return isoString;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

export const formatarCnpjCpf = (valor) => {
  if (!valor) return "";
  const nums = String(valor).replace(/\D/g, "").slice(0, 14);
  if (nums.length === 0) return "";
  if (nums.length <= 11) {
    return nums
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return nums
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

export const parsearCampoAlterado = (campo) => {
  if (campo === null || campo === undefined || campo === "") {
    return { coluna: "", posicao: null, tipo: "" };
  }

  if (typeof campo === "object") {
    return {
      coluna: campo.coluna || campo.nome || campo.campo || "",
      posicao: campo.posicao ?? campo.column_id ?? campo.ordinal ?? null,
      tipo: campo.tipo || campo.data_type || "",
    };
  }

  const texto = String(campo).trim();

  const mColPos = /^(.+?)\s*\(\s*col\.?\s*(\d+)\s*\)\s*$/i.exec(texto);
  if (mColPos) {
    return {
      coluna: mColPos[1].trim(),
      posicao: Number(mColPos[2]),
      tipo: "",
    };
  }

  if (texto.includes("|")) {
    const pedacos = texto.split("|").map((p) => p.trim());
    let posicao = null;
    let inicio = 0;
    if (/^\d+$/.test(pedacos[0])) {
      posicao = Number(pedacos[0]);
      inicio = 1;
    }
    const TIPOS_DADO = new Set([
      "int",
      "integer",
      "float",
      "double",
      "decimal",
      "varchar",
      "char",
      "text",
      "date",
      "datetime",
      "timestamp",
      "boolean",
      "bool",
    ]);
    let tipo = "";
    let fim = pedacos.length;
    if (TIPOS_DADO.has(pedacos[fim - 1].toLowerCase())) {
      tipo = pedacos[fim - 1];
      fim -= 1;
    }
    const coluna = pedacos.slice(inicio, fim).join("|");
    return { coluna, posicao, tipo };
  }

  return { coluna: texto, posicao: null, tipo: "" };
};

export const formatarCampoAlterado = (campo) => {
  const { coluna, posicao } = parsearCampoAlterado(campo);
  if (!coluna) return "—";
  if (posicao) return `${coluna} (col. ${posicao})`;
  return coluna;
};

export const PALAVRAS_CHAVE_MOEDA = [
  "salario",
  "salário",
  "bolsa",
  "auxilio",
  "auxílio",
  "remuneracao",
  "remuneração",
  "valor_bolsa",
  "renda",
  "preco",
  "preço",
  "mensalidade",
  "custo",
];

export const ehChaveMoeda = (campo) => {
  if (!campo) return false;
  const { coluna } = parsearCampoAlterado(campo);
  const normalizado = String(coluna || campo)
    .toLowerCase()
    .trim();
  return PALAVRAS_CHAVE_MOEDA.some((p) => normalizado.includes(p));
};

export const parseDataSegura = (valor) => {
  if (valor instanceof Date) return isNaN(valor.getTime()) ? null : valor;
  if (typeof valor !== "string") return null;
  const s = valor.trim();
  if (!s) return null;
  const iso =
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(s);
  if (iso) {
    const [, y, m, d, hh = "00", mm = "00", ss = "00"] = iso;
    const dt = new Date(
      Number(y),
      Number(m) - 1,
      Number(d),
      Number(hh),
      Number(mm),
      Number(ss),
    );
    return isNaN(dt.getTime()) ? null : dt;
  }
  const br =
    /^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(s);
  if (br) {
    const [, d, m, y, hh = "00", mm = "00", ss = "00"] = br;
    const dt = new Date(
      Number(y),
      Number(m) - 1,
      Number(d),
      Number(hh),
      Number(mm),
      Number(ss),
    );
    return isNaN(dt.getTime()) ? null : dt;
  }
  return null;
};

export const pareceMoeda = (valor) =>
  typeof valor === "string" &&
  (/^\s*[R$€¥£]\s*[\d.,\s]+/.test(valor) || /[\d]+[.,]\d{2}\s*$/.test(valor));

export const pareceNumero = (valor) => {
  if (typeof valor === "number") return Number.isFinite(valor);
  if (typeof valor !== "string") return false;
  const s = valor.trim();
  if (!s) return false;
  // eslint-disable-next-line no-useless-escape
  return /^-?\d{1,3}(?:[\.,]\d{3})*(?:[.,]\d+)?$|^-?\d+(?:[.,]\d+)?$/.test(s);
};

export const paraNumero = (valor) => {
  if (typeof valor === "number") return valor;
  if (typeof valor !== "string") return NaN;
  let s = valor.trim().replace(/[R$€¥£\s]/g, "");
  if (!s) return NaN;
  if (/,\d{1,2}$/.test(s) && s.includes(".")) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (/,\d{1,2}$/.test(s)) {
    s = s.replace(",", ".");
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

export const detectarTipo = (valor, campoAlterado = "") => {
  if (valor === null || valor === undefined || valor === "") return "texto";
  if (campoAlterado && ehChaveMoeda(campoAlterado)) return "moeda";
  if (parseDataSegura(valor)) return "data";
  if (pareceMoeda(valor)) return "moeda";
  if (pareceNumero(valor)) {
    const n = paraNumero(valor);
    return Number.isFinite(n) ? "numero" : "texto";
  }
  return "texto";
};

export const obterValorParaXlsx = (valor, campoAlterado = "") => {
  if (valor === null || valor === undefined || valor === "") {
    return { valor: "", formato: undefined };
  }
  const tipo = detectarTipo(valor, campoAlterado);
  if (tipo === "data") {
    return { valor: parseDataSegura(valor), formato: "dd/mm/yyyy hh:mm" };
  }
  if (tipo === "moeda") {
    return { valor: paraNumero(valor), formato: '"R$" #,##0.00' };
  }
  if (tipo === "numero") {
    return { valor: paraNumero(valor), formato: "#,##0.######" };
  }
  return { valor: String(valor), formato: undefined };
};
